import React, { useCallback, useEffect, useState } from 'react';

import { Box, Button, Grid, LinearProgress } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { DescriptionOutlined } from '@material-ui/icons';
import { Sync, Close } from '@mui/icons-material';
import { Pagination } from '@mui/material';
import moment from 'moment';

import DatePicker from '../../components/common/DatePicker';
import EnhancedTableHead from '../../components/common/EnhancedTableHead';
import SearchInput from '../../components/common/SearchInput';
import { ROLES } from '../../constants/general';
import { ACTIONS } from '../../constants/logs';
import useFetch from '../../hooks/useFetch';
import UserService from '../../services/user.service';
import { sortObjectByText } from '../../utils/general';
import { debounce } from '../../utils/lodashUtils';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'action', label: 'Action' },
  { id: 'userId', label: 'User ID' },
  { id: 'user', label: 'User' },
  { id: 'ip', label: 'IP' },
  { id: 'details', label: 'Details' },
  { id: 'date', label: 'Date' },
  { id: 'role', label: 'Role' },
  { id: 'workspace', label: 'Workspace' },
];

const useStyles = makeStyles((theme) => ({
  actionCell: {
    '&:hover': {
      color: theme.palette.primary.main,
    },

    borderTop: '1px solid #DEDEDE',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '16px',
    padding: '10px',
  },
  closeIcon: {
    '&:hover': {
      color: '#333',
      cursor: 'pointer',
    },
    color: '#aaa',
    display: 'none !important',
    padding: 2,
    position: 'absolute',
    right: 20,
    top: 8,
  },
  inputContainer: {
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  noData: {
    alignItems: 'center',
    color: '#777',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '20px',
    fontWeight: 'bold',
    justifyContent: 'center',
    padding: '32px',
    width: '100%',
  },
  pagination: { '&.MuiButtonBase-root': { backgroundColor: 'red' } },
  paper: { border: '1px solid #DEDEDE', borderRadius: '10px', marginTop: '20px', padding: '0 10px', width: '100%' },
  root: { width: '100%' },
  select: {
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: '8px',
    color: '#999',
    height: '40px',
    padding: '0 16px',
    width: '240px',
  },
  table: { borderSpacing: '0 !important', minWidth: 750 },
  tableCell: {
    '&:hover': {
      cursor: 'default',
    },

    borderTop: '1px solid #DEDEDE',
    color: '#999999',
    fontSize: '14px',
    fontWeight: '400',
    height: '48px',
    lineHeight: '16px',
    padding: '0px 6px !important',
  },
  tableCellClickable: {
    '&:hover': {
      color: theme.palette.primary.main,
    },

    borderTop: '1px solid #DEDEDE',
    color: '#999999',
    fontSize: '14px',
    fontWeight: '400',
    height: '48px',
    lineHeight: '16px',
    padding: '0px 6px !important',
  },
  tableRow: { '&.MuiTableRow-hover:hover': { cursor: 'pointer' } },
  userInput: {
    '&:disabled': {
      backgroundColor: '#F3F3F3',
      color: '#444',
      cursor: 'pointer',
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    backgroundColor: '#F3F3F3',
    border: 'none',
    borderRadius: '8px',
    color: '#999',
    height: '38px',
    padding: '0 16px',
    width: '206px',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

const currentDate = moment().add(1, 'days').format('MM-DD-YYYY');
const lastWeek = moment().subtract(7, 'days').format('MM-DD-YYYY');

export default function LogsContainer() {
  const classes = useStyles();
  const [order, setOrder] = useState('desc');
  const [userList, setUserList] = useState([]);
  const [orderBy, setOrderBy] = useState('date');
  const [page, setPage] = useState(0);
  const [startDate, setStartDate] = useState(lastWeek);
  const [endDate, setEndDate] = useState(currentDate);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [query, setQuery] = useState({ endDate: currentDate, startDate: lastWeek });

  const { data: workspaces } = useFetch('/workspaces', { minimalInfo: true, pagination: false });
  const { data: logs, handleFetch: searchLogs, loading } = useFetch('/logs', query);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleSearch = async () => {
    await searchLogs();
  };

  const resetFilters = () => {
    resetStartDateFilter();
    resetEndDateFilter();
    resetActionFilter();
    resetRoleFilter();
    setPage(0);
    setOrder('desc');
    setSelectedUser(null);
    resetWorkspaceFilter();
    setQuery({});
  };

  const handleRowsChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userSearch = useCallback(async (name = '') => {
    try {
      if (name.length < 3) return;

      const userService = new UserService();

      const users = await userService.searchUsers(name);
      setUserList(users.map((user) => ({ key: user.id, label: `${user.id} - ${user.name}`, value: user })));
    } catch (error) {
      console.log('userSearch ~ error:', error);
    }
  }, []);

  const handleUserSelection = (user) => {
    setSelectedUser(user);
    setQuery({ ...query, userId: user.id });
  };

  const removeSelectedUser = () => {
    setSelectedUser(null);
    setQuery({ ...query, userId: undefined });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchUser = useCallback(debounce(userSearch, 500), []);

  // Actions
  const handleActionChange = (event) => {
    addActionAsFilter(event.target.value);
  };

  const addActionAsFilter = (action) => {
    setQuery({ ...query, action });
    setSelectedAction(action);
  };

  const resetActionFilter = () => {
    setSelectedAction('');
    setQuery({ ...query, action: undefined });
  };

  // Roles
  const handleRoleChange = (event) => {
    addRoleAsFilter(event.target.value);
  };

  const addRoleAsFilter = (role) => {
    setQuery({ ...query, role });
    setSelectedRole(role);
  };

  const resetRoleFilter = () => {
    setSelectedRole('');
    setQuery({ ...query, role: undefined });
  };

  // Workspaces
  const handleWorkspaceChange = (event) => {
    addWorkspaceAsFilter(event.target.value);
  };

  const addWorkspaceAsFilter = (workspace) => {
    setQuery({ ...query, workspace });
    setSelectedWorkspace(workspace);
  };

  const resetWorkspaceFilter = () => {
    setSelectedWorkspace('');
    setQuery({ ...query, workspace: undefined });
  };

  // Dates
  const addStartDateChange = (date) => {
    setStartDate(date);
  };

  const resetStartDateFilter = () => {
    setStartDate(lastWeek);
    setQuery({ ...query, startDate: lastWeek });
  };

  const addEndDateChange = (date) => {
    setEndDate(date);
  };

  const resetEndDateFilter = () => {
    setEndDate(currentDate);
    setQuery({ ...query, endDate: currentDate });
  };

  useEffect(() => {
    setQuery({ ...query, endDate, startDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const prepareIp = (ip) => {
    if (!ip) return '';
    if (ip === '::1' || ip === '127.0.0.1') return 'Localhost';

    return ip.replace('::ffff:', '');
  };

  return (
    <div className={classes.root}>
      <Grid container style={{ padding: '40px 4rem' }}>
        <Grid item xs={12} style={{ paddingLeft: '8px' }}>
          <Typography variant="h4" align="left" className="page-header mb-1">
            Activity Logs
          </Typography>
        </Grid>

        <Box width="100%" display="flex" flexDirection="column" gridGap={20}>
          <Box width="100%" display="flex" flexWrap="wrap" gridGap={16} alignItems="center">
            <Box width="min(380px, 100%)">
              <SearchInput onSearch={searchUser} onItemClick={handleUserSelection} onKeywordUpdate={searchUser} items={userList} placeholder="Search User" disableSearch />
            </Box>

            <div>
              <select style={{ width: '100px' }} className={classes.select} id="roleSelect" value={rowsPerPage} onChange={handleRowsChange}>
                {[10, 25, 50].map((pages) => (
                  <option key={pages} value={pages}>
                    {pages}
                  </option>
                ))}
              </select>
            </div>

            <DatePicker displayDate={startDate} onChange={(date) => addStartDateChange(date)} defaultText="Start Date" />
            <DatePicker displayDate={endDate} onChange={(date) => addEndDateChange(date)} defaultText="End Date" />
          </Box>

          <Box alignItems="center" width="100%" display="flex" flexWrap="wrap" gridGap={16}>
            <div className={classes.inputContainer}>
              <input className={classes.userInput} type="text" placeholder="User" max={24} value={selectedUser?.name || ''} disabled />

              {!!selectedUser && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={removeSelectedUser} />}
            </div>

            <div className={classes.inputContainer}>
              <select className={classes.select} id="actionSelect" value={selectedAction} label="Action" onChange={handleActionChange}>
                <option disabled defaultValue value="">
                  Actions
                </option>
                {Object.values(ACTIONS).map((action) => (
                  <option key="action" value={action.label}>
                    {action}
                  </option>
                ))}
              </select>

              {!!selectedAction && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetActionFilter} />}
            </div>

            <div className={classes.inputContainer}>
              <select className={classes.select} id="roleSelect" value={selectedRole} onChange={handleRoleChange}>
                <option disabled defaultValue value="">
                  Roles
                </option>
                {Object.values(ROLES).map((role) => (
                  <option key={role.label} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>

              {!!selectedRole && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetRoleFilter} />}
            </div>

            <div className={classes.inputContainer}>
              <select className={classes.select} id="workspaceSelect" value={selectedWorkspace} label="Action" onChange={handleWorkspaceChange}>
                <option disabled defaultValue value="">
                  Workspaces
                </option>
                {sortObjectByText(workspaces, 'name').map((workspace) => (
                  <option key={workspace.name} value={workspace.name}>
                    {workspace.name}
                  </option>
                ))}
              </select>

              {!!selectedWorkspace && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetWorkspaceFilter} />}
            </div>

            <Box display="flex" gridGap={16}>
              <Button color="primary" style={{ height: '40px', width: '130px' }} variant="contained" size="medium" onClick={handleSearch}>
                Search
              </Button>
              <Button color="secondary" variant="outlined" startIcon={<Sync />} style={{ height: '40px', width: '130px' }} size="medium" onClick={resetFilters}>
                Reset
              </Button>
            </Box>
          </Box>
        </Box>

        <Paper className={classes.paper}>
          <TableContainer>
            <Table className={[classes.table]} aria-labelledby="tableTitle">
              <EnhancedTableHead classes={classes} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} tableHeaders={headCells} />

              <TableBody>
                {stableSort(logs, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow className={classes.tableRow} hover role="checkbox" tabIndex={-1} key={row.name}>
                        <TableCell
                          className={classes.actionCell}
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                          width={206}
                          onClick={() => addActionAsFilter(row.action)}
                          title="Add action as filter">
                          {row.action}
                        </TableCell>
                        <TableCell
                          className={classes.tableCellClickable}
                          align="left"
                          title="Add user as filter"
                          width={100}
                          onClick={() => handleUserSelection({ id: row.userId, name: row.user })}>
                          {row.userId}
                        </TableCell>
                        <TableCell
                          className={classes.tableCellClickable}
                          align="left"
                          title="Add user as filter"
                          width={160}
                          onClick={() => handleUserSelection({ id: row.userId, name: row.user })}>
                          {row.user}
                        </TableCell>
                        <TableCell className={classes.tableCell} align="left" width={100}>
                          {prepareIp(row.ip)}
                        </TableCell>
                        <TableCell className={classes.tableCell} align="left" width={206}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            gridGap={4}
                            padding="12px 0"
                            maxWidth={300}
                            lineHeight={1.5}
                            textWrap="wrap"
                            textOverflow="clip"
                            wordWrap="break-word">
                            {Object.entries(row?.details || {}).map((entry) =>
                              entry[1] || [0, 1].includes(entry[1]) || entry[1] === false ? (
                                <Box display="flex" flexDirection="column">
                                  <div style={{ overflowWrap: 'break-word', textOverflow: 'wrap' }}>
                                    <b>{entry[0]}:</b> {String(entry[1])}
                                  </div>
                                </Box>
                              ) : (
                                ''
                              )
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="left" width={140}>
                          {moment(row.date).format('MM-DD-YYYY, hh:mm a')}
                        </TableCell>
                        <TableCell className={classes.tableCellClickable} align="left" width={100} onClick={() => addRoleAsFilter(row.role)} title="Add role as filter">
                          {row.role}
                        </TableCell>
                        <TableCell
                          className={classes.tableCellClickable}
                          align="left"
                          width={160}
                          onClick={() => addWorkspaceAsFilter(row.workspace)}
                          title="Add workspace as filter">
                          {row.workspace}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {logs.length === 0 && loading && (
          <div className={classes.noData}>
            Loading
            <LinearProgress color="primary" style={{ height: '2px', width: 'min(400px, 100%)' }} />
          </div>
        )}

        {logs.length === 0 && !loading && (
          <div className={classes.noData}>
            <DescriptionOutlined />
            No logs found
          </div>
        )}

        {logs.length > 0 && (
          <Box width="100%" display="flex" justifyContent="right" alignItems="center" marginTop={2.5}>
            <Pagination className={classes.pagination} count={logs.length ? Math.ceil(logs.length / rowsPerPage) : 1} page={page + 1} onChange={handleChangePage} />
          </Box>
        )}
      </Grid>
    </div>
  );
}
